import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash2, Pencil, Phone, Mail, Percent } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import EmployeeClosing from "@/pages/EmployeeClosing";

const money = (value) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(value || 0));

const hasAmount = (value) => Number(value || 0) > 0;

function remunerationSummary(emp) {
  const comp = emp.compensation || {};
  const type = comp.type || "comissao";
  const parts = [];

  if (hasAmount(comp.weekly_amount)) parts.push(`semana ${money(comp.weekly_amount)}`);
  if (hasAmount(comp.biweekly_amount)) parts.push(`quinzena ${money(comp.biweekly_amount)}`);
  if (hasAmount(comp.monthly_amount)) parts.push(`mês ${money(comp.monthly_amount)}`);
  if (hasAmount(comp.daily_amount)) parts.push(`diária ${money(comp.daily_amount)}`);

  if (type === "salario_fixo") {
    return { title: "Salário fixo", detail: parts.join(" · ") || "valor não definido" };
  }
  if (type === "salario_mais_comissao") {
    return { title: "Salário + comissão", detail: `${parts.join(" · ") || "salário não definido"} · percentual por serviço` };
  }
  if (type === "diaria") {
    return { title: "Diária", detail: parts.join(" · ") || "valor não definido" };
  }
  if (type === "ajuda_custo") {
    return { title: "Ajuda de custo", detail: parts.join(" · ") || "valor não definido" };
  }
  const serviceCount = (comp.commission_service_ids || []).length;
  return {
    title: "Comissão por serviço",
    detail: serviceCount > 0
      ? `${serviceCount} serviço(s) habilitado(s)`
      : "percentual vem do cadastro de Serviços",
  };
}

export default function Employees() {
  const { apiCall } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", role: "", commission_rate: "0", active: true,
    participates_in_agenda: true,
    comp_type: "comissao", comp_weekly: "0", comp_biweekly: "0",
    comp_monthly: "0", comp_daily: "0", comp_notes: "", comp_auto: true,
    comp_service_ids: [], // iter64: serviços onde ela ganha comissão
  });

  // iter64: lista de serviços cadastrados para o checklist de comissão
  const [servicesCatalog, setServicesCatalog] = useState([]);
  const [servicesSearch, setServicesSearch] = useState("");
  useEffect(() => {
    (async () => {
      try {
        const r = await apiCall("get", "/services");
        setServicesCatalog(Array.isArray(r.data) ? r.data : []);
      } catch { /* silencioso */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // iter63: modal de inativação com migração
  const [deactModal, setDeactModal] = useState(null); // { employee, dependencies, migrations: {appointment_id: {action, new_employee_id}}, force }

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiCall("get", "/employees");
      setEmployees(res.data);
    } catch {
      toast.error("Erro ao carregar funcionários");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleOpenNew = () => {
    setEditing(null);
    setForm({
      name: "", email: "", phone: "", role: "", commission_rate: "0", active: true,
      participates_in_agenda: true,
      comp_type: "comissao", comp_weekly: "0", comp_biweekly: "0",
      comp_monthly: "0", comp_daily: "0", comp_notes: "", comp_auto: true,
      comp_service_ids: [],
    });
    setOpen(true);
  };

  const handleEdit = (emp) => {
    setEditing(emp);
    const c = emp.compensation || {};
    setForm({
      name: emp.name,
      email: emp.email || "",
      phone: emp.phone || "",
      role: emp.role || "",
      commission_rate: emp.commission_rate?.toString() || "0",
      active: emp.active !== false,
      participates_in_agenda: emp.participates_in_agenda !== false,
      comp_type: c.type || "comissao",
      comp_weekly: (c.weekly_amount ?? 0).toString(),
      comp_biweekly: (c.biweekly_amount ?? 0).toString(),
      comp_monthly: (c.monthly_amount ?? 0).toString(),
      comp_daily: (c.daily_amount ?? 0).toString(),
      comp_notes: c.notes || "",
      comp_auto: c.auto_include_in_closing !== false,
      comp_service_ids: Array.isArray(c.commission_service_ids) ? c.commission_service_ids : [],
    });
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // iter63: se está EDITANDO e tentando desativar um ativo, precisa checar pendências
      if (editing && editing.active !== false && !form.active) {
        const dep = await apiCall("get", `/employees/${editing.employee_id}/dependencies`);
        if ((dep.data?.pending_appointments_count || 0) > 0) {
          // Abre modal de migração ao invés de salvar direto
          const initialMig = {};
          (dep.data.pending_appointments || []).forEach((ap) => {
            initialMig[ap.appointment_id] = { action: "reassign", new_employee_id: "" };
          });
          setDeactModal({
            employee: editing,
            dependencies: dep.data,
            migrations: initialMig,
            force: false,
            pendingForm: { ...form },
          });
          return;
        }
      }
      const payload = {
        name: form.name, email: form.email || null, phone: form.phone,
        role: form.role, active: form.active,
        participates_in_agenda: form.participates_in_agenda,
        commission_rate: 0,
        compensation: {
          type: form.comp_type,
          weekly_amount: parseFloat(form.comp_weekly) || 0,
          biweekly_amount: parseFloat(form.comp_biweekly) || 0,
          monthly_amount: parseFloat(form.comp_monthly) || 0,
          daily_amount: parseFloat(form.comp_daily) || 0,
          notes: form.comp_notes || null,
          auto_include_in_closing: form.comp_auto,
          commission_service_ids: form.comp_service_ids || [],
        },
      };
      if (editing) {
        await apiCall("put", `/employees/${editing.employee_id}`, payload);
        toast.success("Funcionário atualizado");
      } else {
        await apiCall("post", "/employees", payload);
        toast.success("Funcionário criado");
      }
      setOpen(false);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // iter65: se o backend bloqueou por pendentes, dispara o modal de migração
      if (err?.response?.status === 409 && detail && typeof detail === "object" && detail.reason === "employee_has_pending_appointments" && editing) {
        try {
          const dep = await apiCall("get", `/employees/${editing.employee_id}/dependencies`);
          const initialMig = {};
          (dep.data.pending_appointments || []).forEach((ap) => {
            initialMig[ap.appointment_id] = { action: "reassign", new_employee_id: "" };
          });
          setDeactModal({
            employee: editing,
            dependencies: dep.data,
            migrations: initialMig,
            force: false,
            pendingForm: { ...form },
          });
          toast.info(`${detail.pending_count} agendamento(s) pendente(s) — escolha o destino de cada um.`);
        } catch {
          toast.error("Backend bloqueou a inativação mas não conseguiu abrir o modal. Recarregue a página.");
        }
        return;
      }
      toast.error(typeof detail === "string" ? detail : "Erro ao salvar");
    }
  };

  // iter63: confirma migração e inativa
  const confirmDeactivation = async () => {
    if (!deactModal) return;
    const migrations = Object.entries(deactModal.migrations).map(([appointment_id, m]) => ({
      appointment_id,
      action: m.action,
      new_employee_id: m.action === "reassign" ? m.new_employee_id : null,
    }));
    // Valida: cada reassign precisa de new_employee_id
    for (const m of migrations) {
      if (m.action === "reassign" && !m.new_employee_id) {
        toast.error("Escolha o funcionário destino para todos os agendamentos ou marque 'Cancelar'");
        return;
      }
    }
    try {
      const r = await apiCall("post", `/employees/${deactModal.employee.employee_id}/deactivate-with-migration`, {
        migrations,
        force: deactModal.force,
      });
      toast.success(
        `Inativado · ${r.data.reassigned} transferido(s), ${r.data.cancelled} cancelado(s)` +
        (r.data.uncovered_forced ? ` · ${r.data.uncovered_forced} órfão(s) forçado(s)` : "")
      );
      setDeactModal(null);
      setOpen(false);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.reason === "uncovered_pending_appointments") {
        toast.error(`Faltam ${detail.count} agendamentos. Trate todos ou use forçar.`);
      } else {
        toast.error(typeof detail === "string" ? detail : "Erro ao inativar");
      }
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Excluir este funcionário?")) return;
    try {
      await apiCall("delete", `/employees/${id}`);
      toast.success("Funcionário excluído");
      load();
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  return (
    <div className="space-y-8" data-testid="employees-page">
      <PageHeader overline="Equipe" title="Funcionários" subtitle="Gerencie sua equipe, taxas e fechamentos">
        <Button onClick={handleOpenNew} data-testid="add-employee-button">
          <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
          Novo Funcionário
        </Button>
      </PageHeader>

      <Tabs defaultValue="cadastro" className="space-y-6">
        <TabsList data-testid="employees-tabs">
          <TabsTrigger value="cadastro" data-testid="tab-cadastro">Cadastro</TabsTrigger>
          <TabsTrigger value="fechamento" data-testid="tab-fechamento">Fechamento / Pagamento</TabsTrigger>
        </TabsList>

        <TabsContent value="cadastro">
          <Card className="border border-border bg-card shadow-soft rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/30 hover:bg-secondary/30">
              <TableHead className="text-xs uppercase tracking-widest font-medium">Nome</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Função</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Contato</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Regra de pagamento</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Status</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-widest font-medium">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
            ) : employees.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">Nenhum funcionário cadastrado.</TableCell></TableRow>
            ) : (
              employees.map((emp) => (
                <TableRow key={emp.employee_id} data-testid={`employee-row-${emp.employee_id}`}>
                  <TableCell className="font-medium">{emp.name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{emp.role || "—"}</TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      {emp.phone && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="h-3 w-3 text-muted-foreground" strokeWidth={1.5} />
                          {emp.phone}
                        </div>
                      )}
                      {emp.email && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Mail className="h-3 w-3" strokeWidth={1.5} />
                          {emp.email}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const pay = remunerationSummary(emp);
                      return (
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-1 font-medium text-sm" style={{ color: "#CBA871" }}>
                            <Percent className="h-3 w-3" strokeWidth={1.5} />
                            {pay.title}
                          </div>
                          <div className="text-[11px] text-muted-foreground leading-snug">
                            {pay.detail}
                          </div>
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-xs uppercase tracking-widest font-medium" style={{ color: emp.active ? "#166534" : "#737373" }}>
                        {emp.active ? "Ativo" : "Inativo"}
                      </span>
                      {emp.participates_in_agenda === false && (
                        <span
                          className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 border border-slate-300 text-slate-700"
                          data-testid={`no-agenda-badge-${emp.employee_id}`}
                          title="Não aparece como coluna na Agenda"
                        >
                          fora da agenda
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(emp)} data-testid={`edit-employee-${emp.employee_id}`}>
                      <Pencil className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(emp.employee_id)} data-testid={`delete-employee-${emp.employee_id}`}>
                      <Trash2 className="h-4 w-4 text-destructive" strokeWidth={1.5} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
        </TabsContent>

        <TabsContent value="fechamento">
          <EmployeeClosing />
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-light tracking-tight text-2xl">
              {editing ? "Editar Funcionário" : "Novo Funcionário"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label className="text-xs uppercase tracking-widest">Nome</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="employee-name-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Função</Label>
              <Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="Ex: Cabeleireira, Manicure" data-testid="employee-role-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-widest">Telefone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="employee-phone-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="employee-email-input" />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-widest">Funcionário ativo</Label>
              <Switch checked={form.active} onCheckedChange={(c) => setForm({ ...form, active: c })} data-testid="employee-active-switch" />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Label className="text-xs uppercase tracking-widest">Aparece na Agenda</Label>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  Desmarque para funcionários que não recebem clientes (recepção, admin, faxina). Continua sendo pago normalmente.
                </p>
              </div>
              <Switch
                checked={form.participates_in_agenda}
                onCheckedChange={(c) => setForm({ ...form, participates_in_agenda: c })}
                data-testid="employee-agenda-switch"
              />
            </div>

            {/* Configuração de remuneração — iter64: campos aparecem
                condicionalmente conforme o tipo. Comissão usa CHECKLIST
                de serviços cadastrados; % é herdado do próprio serviço. */}
            <div className="border-t border-border pt-4 space-y-3">
              <div className="text-xs uppercase tracking-widest font-medium text-muted-foreground">Remuneração</div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Tipo</Label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  value={form.comp_type}
                  onChange={(e) => setForm({ ...form, comp_type: e.target.value })}
                  data-testid="employee-comp-type"
                >
                  <option value="comissao">Comissão (por serviço)</option>
                  <option value="salario_fixo">Salário fixo</option>
                  <option value="salario_mais_comissao">Salário + comissão por serviço</option>
                  <option value="diaria">Diária</option>
                  <option value="ajuda_custo">Ajuda de custo</option>
                </select>
              </div>

              {/* Bloco SALÁRIO — valores explicitamente cadastrados prevalecem no fechamento. */}
              {["salario_fixo", "salario_mais_comissao", "ajuda_custo"].includes(form.comp_type) && (
                <div className="space-y-2">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs uppercase tracking-widest">Semanal (R$)</Label>
                      <Input type="number" step="0.01" min="0" value={form.comp_weekly}
                        onChange={(e) => setForm({ ...form, comp_weekly: e.target.value })}
                        data-testid="employee-comp-weekly"
                        placeholder="Ex: 500,00" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-widest">Quinzenal (R$)</Label>
                      <Input type="number" step="0.01" min="0" value={form.comp_biweekly}
                        onChange={(e) => setForm({ ...form, comp_biweekly: e.target.value })}
                        data-testid="employee-comp-biweekly"
                        placeholder="Ex: 1000,00" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-widest">Mensal (R$)</Label>
                      <Input type="number" step="0.01" min="0" value={form.comp_monthly}
                        onChange={(e) => setForm({ ...form, comp_monthly: e.target.value })}
                        data-testid="employee-comp-monthly"
                        placeholder="Ex: 2000,00" />
                    </div>
                  </div>
                  <p className="text-[10px] text-muted-foreground">
                    Cadastre o valor na periodicidade real do combinado. No fechamento, o sistema usa o valor correspondente ao período escolhido; se faltar, deriva a partir dos outros campos.
                  </p>
                </div>
              )}

              {/* Bloco DIÁRIA */}
              {form.comp_type === "diaria" && (
                <div>
                  <Label className="text-xs uppercase tracking-widest">Valor diária (R$)</Label>
                  <Input type="number" step="0.01" min="0" value={form.comp_daily}
                    onChange={(e) => setForm({ ...form, comp_daily: e.target.value })}
                    data-testid="employee-comp-daily" />
                </div>
              )}

              {/* Bloco COMISSÃO — checklist de serviços cadastrados.
                  Aparece quando é comissão pura OU salário + comissão. */}
              {(form.comp_type === "comissao" || form.comp_type === "salario_mais_comissao") && (
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest flex items-center justify-between">
                    <span>Serviços que geram comissão para este funcionário</span>
                    <span className="text-[10px] font-normal text-muted-foreground normal-case tracking-normal">
                      {(form.comp_service_ids || []).length} selecionado(s) de {servicesCatalog.length}
                    </span>
                  </Label>
                  <Input
                    placeholder="Buscar serviço…"
                    value={servicesSearch}
                    onChange={(e) => setServicesSearch(e.target.value)}
                    className="h-8 text-xs"
                    data-testid="employee-comp-services-search"
                  />
                  <div className="border rounded-md max-h-56 overflow-y-auto bg-background" data-testid="employee-comp-services-list">
                    {servicesCatalog.length === 0 && (
                      <div className="p-3 text-center text-xs text-muted-foreground">
                        Nenhum serviço cadastrado — vá em <span className="font-medium">Serviços</span> primeiro.
                      </div>
                    )}
                    {servicesCatalog
                      .filter(s => !servicesSearch || (s.name || "").toLowerCase().includes(servicesSearch.toLowerCase()))
                      .map((s) => {
                        const sid = s.service_id;
                        const checked = (form.comp_service_ids || []).includes(sid);
                        const commPct = s.commission_percent ?? s.commission_rate ?? null;
                        return (
                          <label
                            key={sid}
                            className={`flex items-center gap-2 p-2 border-b last:border-0 text-xs cursor-pointer hover:bg-accent/50 ${checked ? "bg-emerald-50/40" : ""}`}
                            data-testid={`comp-svc-row-${sid}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => {
                                const cur = form.comp_service_ids || [];
                                const next = checked ? cur.filter(x => x !== sid) : [...cur, sid];
                                setForm({ ...form, comp_service_ids: next });
                              }}
                              data-testid={`comp-svc-check-${sid}`}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium truncate">{s.name}</div>
                              {s.category && (
                                <div className="text-[10px] text-muted-foreground">{s.category}</div>
                              )}
                            </div>
                            <div className="text-[10px] text-muted-foreground shrink-0">
                              {commPct !== null && commPct !== undefined
                                ? `${commPct}%`
                                : <span className="text-amber-700">% não cadastrado</span>}
                            </div>
                          </label>
                        );
                      })}
                  </div>
                  <p className="text-[10px] text-muted-foreground">
                    O percentual vem do cadastro do serviço. Aqui você só marca quais serviços geram comissão para esta pessoa.
                  </p>
                  {servicesCatalog.length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-1">
                      <button
                        type="button"
                        onClick={() => {
                          const allIds = servicesCatalog.map(s => s.service_id);
                          setForm({ ...form, comp_service_ids: allIds });
                        }}
                        className="text-[10px] px-2 py-0.5 border rounded hover:bg-accent"
                      >
                        Marcar todos
                      </button>
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, comp_service_ids: [] })}
                        className="text-[10px] px-2 py-0.5 border rounded hover:bg-accent"
                      >
                        Limpar seleção
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div>
                <Label className="text-xs uppercase tracking-widest">Observação</Label>
                <Input value={form.comp_notes}
                  onChange={(e) => setForm({ ...form, comp_notes: e.target.value })}
                  placeholder="Ex: Regra especial, período de teste, etc"
                  data-testid="employee-comp-notes" />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-xs uppercase tracking-widest">Incluir no fechamento automaticamente</Label>
                  <p className="text-[10px] text-muted-foreground">Base salário aparece automaticamente no Fechamento.</p>
                </div>
                <Switch checked={form.comp_auto}
                  onCheckedChange={(c) => setForm({ ...form, comp_auto: c })}
                  data-testid="employee-comp-auto" />
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="employee-save-button">{editing ? "Atualizar" : "Criar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* iter63: Modal de inativação com migração de agenda */}
      <Dialog open={!!deactModal} onOpenChange={(o) => !o && setDeactModal(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="deactivate-migration-dialog">
          <DialogHeader>
            <DialogTitle>
              Inativar {deactModal?.employee?.name}: transferir agenda pendente
            </DialogTitle>
          </DialogHeader>
          {deactModal && (
            <div className="space-y-3">
              <div className="text-sm bg-amber-50 border border-amber-200 rounded p-3">
                Este funcionário tem <strong>{deactModal.dependencies.pending_appointments_count}</strong> agendamento(s) pendente(s).
                Para cada um, escolha <strong>transferir</strong> para outro profissional (padrão) ou <strong>cancelar</strong>.
                Agendamentos passados e comissões acumuladas permanecem intactos.
              </div>
              <div className="border rounded-md max-h-96 overflow-y-auto">
                {(deactModal.dependencies.pending_appointments || []).map((ap) => {
                  const m = deactModal.migrations[ap.appointment_id] || {};
                  return (
                    <div key={ap.appointment_id} className="p-3 border-b last:border-0 text-xs" data-testid={`deact-row-${ap.appointment_id}`}>
                      <div className="flex flex-wrap justify-between gap-2">
                        <div>
                          <div className="font-medium text-sm">{ap.client_name || "(cliente sem nome)"}</div>
                          <div className="text-[10px] text-muted-foreground">
                            {ap.service_name || "—"} · {new Date(ap.date).toLocaleString("pt-BR")} · {ap.status}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <select
                            value={m.action}
                            onChange={(e) => setDeactModal((s) => ({
                              ...s,
                              migrations: { ...s.migrations, [ap.appointment_id]: { ...m, action: e.target.value } },
                            }))}
                            className="h-8 text-xs border rounded px-2 bg-background"
                            data-testid={`deact-action-${ap.appointment_id}`}
                          >
                            <option value="reassign">Transferir</option>
                            <option value="cancel">Cancelar</option>
                          </select>
                          {m.action === "reassign" && (
                            <select
                              value={m.new_employee_id || ""}
                              onChange={(e) => setDeactModal((s) => ({
                                ...s,
                                migrations: { ...s.migrations, [ap.appointment_id]: { ...m, new_employee_id: e.target.value } },
                              }))}
                              className="h-8 text-xs border rounded px-2 bg-background min-w-40"
                              data-testid={`deact-target-${ap.appointment_id}`}
                            >
                              <option value="">— Escolher funcionário —</option>
                              {(deactModal.dependencies.available_replacements || []).map((r) => (
                                <option key={r.employee_id} value={r.employee_id}>{r.name}</option>
                              ))}
                            </select>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Atalhos em massa */}
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="text-muted-foreground">Atalhos:</span>
                <select
                  onChange={(e) => {
                    const targetId = e.target.value;
                    if (!targetId) return;
                    setDeactModal((s) => {
                      const next = { ...s.migrations };
                      Object.keys(next).forEach((k) => {
                        next[k] = { action: "reassign", new_employee_id: targetId };
                      });
                      return { ...s, migrations: next };
                    });
                    e.target.value = "";
                  }}
                  className="h-8 text-xs border rounded px-2 bg-background"
                  data-testid="deact-bulk-target"
                  defaultValue=""
                >
                  <option value="">Transferir TUDO para…</option>
                  {(deactModal.dependencies.available_replacements || []).map((r) => (
                    <option key={r.employee_id} value={r.employee_id}>{r.name}</option>
                  ))}
                </select>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => setDeactModal((s) => {
                    const next = { ...s.migrations };
                    Object.keys(next).forEach((k) => { next[k] = { action: "cancel", new_employee_id: null }; });
                    return { ...s, migrations: next };
                  })}
                  data-testid="deact-bulk-cancel"
                >
                  Cancelar todos
                </Button>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeactModal(null)}>Voltar</Button>
            <Button onClick={confirmDeactivation} data-testid="deact-confirm-btn">
              Confirmar inativação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
